/**
 * Cuándo se aceptan las dos fotos del DNI.
 *
 * El criterio es del usuario (2026-10-13): «que se puedan extraer los datos
 * importantes de manera confiable, que se reconozca que son 2 caras del mismo
 * DNI y que sean legibles por los humanos de SADAIC los datos básicos». Son tres
 * condiciones, y las tres se miden sobre lo que la lectura sacó de cada cara:
 *
 *   (a) datosConfiables — los datos básicos están, ninguno en conflicto, y el
 *       número y el nacimiento quedaron probados (dígito de control, código de
 *       barras) o confirmados por dos fuentes.
 *   (b) mismoDocumento — el número del frente es el del dorso, y el apellido y
 *       los nombres que dan las fuentes legibles por máquina de cada cara no se
 *       contradicen. `null` cuando no hay con qué comparar.
 *   (c) legibles — en alguna de las dos caras se leyeron los cuatro datos
 *       básicos. Es lo que se exigía antes de los tickets KAN-452..457: una
 *       persona de SADAIC tiene que poder leerlos, y alcanza con una cara.
 *
 * El tapado del Nº de Trámite NO es criterio de aceptación: se tapa cuando la
 * ubicación lo garantiza (ver frenteSinTramitePuedeSeguir).
 */
import { Hallazgo, consolidar } from "./reader/hallazgos";
import { distancia } from "./reader/texto";
import type { CampoLeido } from "./reader/types";

/** Los datos que un empleado de SADAIC tiene que poder corroborar. */
export const DATOS_BASICOS = ["apellido", "nombres", "numeroDocumento", "fechaNacimiento"] as const;

export type Lado = "anverso" | "reverso";

export interface Aceptacion {
  datosConfiables: boolean;
  mismoDocumento: boolean | null;
  legibles: boolean;
  aceptada: boolean;
  /** La cara de la que hay que pedir otra foto, o null si se aceptan. */
  pedirOtra: Lado | null;
}

const PROBADA = new Set(["digito_de_control", "coincidencia_multiple"]);
const soloDigitos = (v: string) => v.replace(/\D/g, "").replace(/^0+/, "");
const soloLetras = (v: string) =>
  v.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().replace(/[^A-Z]/g, "");

/** Si una cara trae los cuatro datos básicos, de cualquier fuente. */
export function caraConDatosBasicos(hallazgos: Hallazgo[]): boolean {
  return DATOS_BASICOS.every((campo) => hallazgos.some((h) => h.campo === campo && h.valor.trim() !== ""));
}

/**
 * Dos lecturas del mismo nombre: se comparan en el largo común —el MRZ trunca a 30
 * caracteres y el OCR lee su relleno «<» como K o L al final— con una letra de
 * tolerancia cada cuatro. Medido sobre el corpus: el MRZ de un dorso devolvió el
 * nombre con quince letras de relleno pegadas, y un apellido con dos letras mal.
 */
function nombresCoherentes(a: string, b: string): boolean {
  const x = soloLetras(a);
  const y = soloLetras(b);
  const largo = Math.min(x.length, y.length);
  if (largo === 0) return true;
  const tope = Math.floor(largo / 4) + 1;
  return distancia(x.slice(0, largo), y.slice(0, largo), tope) <= tope;
}

/** El valor del campo según las fuentes legibles por máquina de esta cara (sin el OCR del impreso). */
function deMaquina(hallazgos: Hallazgo[], campo: string): string | null {
  const propios = hallazgos.filter((h) => h.campo === campo && h.origen !== "impreso");
  return consolidar(propios)[campo]?.valor ?? null;
}

/**
 * Cuántos dígitos puede errar el OCR del impreso antes de que la diferencia deje de
 * ser ruido y pase a ser otro dato: dos (re-revisión, ronda 2). Con uno, dos
 * dígitos mal leídos rechazaban un par bueno.
 */
const RUIDO_DE_OCR = 2;

/**
 * Si la diferencia entre lo leído sin prueba y lo probado es ruido del OCR. Un
 * número: hasta RUIDO_DE_OCR dígitos distintos. Una fecha: distinta en un solo
 * campo (día, mes o año), aunque en ese campo cambien varios dígitos.
 */
function esRuido(campo: string, a: string, b: string): boolean {
  if (campo === "fechaNacimiento") {
    const fa = a.split("-");
    const fb = b.split("-");
    if (fa.length === 3 && fb.length === 3) return fa.filter((x, i) => x !== fb[i]).length <= 1;
  }
  const da = soloDigitos(a);
  const db = soloDigitos(b);
  return distancia(da, db, RUIDO_DE_OCR) <= RUIDO_DE_OCR;
}

/**
 * Lo que una cara dice de un campo: su valor consolidado, si está probado o
 * confirmado por dos fuentes, y si lo prueba un dígito de control.
 */
function deLaCara(
  hallazgos: Hallazgo[],
  campo: string
): { valor: string; probado: boolean; porDigito: boolean } | null {
  const c = consolidar(hallazgos)[campo];
  return c?.valor
    ? { valor: c.valor, probado: PROBADA.has(c.verificacion), porDigito: c.verificacion === "digito_de_control" }
    : null;
}

/** La contradicción en un campo de identidad, y qué cara es la sospechosa. */
type Contradiccion = { cara: Lado } | null;

/**
 * Si las dos caras se contradicen en un campo numérico (el número, el nacimiento).
 *
 * Probado contra probado: cualquier diferencia; la sospechosa es el dorso, la
 * última foto. Probado contra leído sin prueba: solo si no es ruido del OCR
 * (esRuido); la sospechosa es la cara sin prueba. Revisión, importante 2: tomar
 * siempre lo impreso como ruido dejaba pasar un frente con otro número y otra
 * fecha frente a un MRZ probado.
 */
function contradiccionEn(frente: Hallazgo[], dorso: Hallazgo[], campo: string): Contradiccion {
  const f = deLaCara(frente, campo);
  const d = deLaCara(dorso, campo);
  if (!f || !d || (!f.probado && !d.probado)) return null;
  const a = soloDigitos(f.valor);
  const b = soloDigitos(d.valor);
  if (a === b) return null;
  if (!(f.probado && d.probado) && esRuido(campo, f.valor, d.valor)) return null;
  // La sospechosa es la menos probada: dígito de control, luego dos fuentes, luego una lectura.
  const peso = (x: { probado: boolean; porDigito: boolean }) => (x.porDigito ? 2 : x.probado ? 1 : 0);
  return { cara: peso(d) > peso(f) ? "anverso" : "reverso" };
}

/**
 * (b): ¿son las dos caras del mismo documento?
 *
 * Decide el NÚMERO, que es la identidad del documento, junto con el nacimiento:
 * una contradicción en cualquiera de los dos (ver contradiccionEn) dice que no.
 * El mismo número en las dos caras dice que sí. Sin números para comparar,
 * deciden el apellido y los nombres de una fuente de máquina de cada cara
 * (código de barras, MRZ), con tolerancia: el MRZ no tiene dígito de control para
 * los nombres y el OCR los ensucia. Si nada se puede comparar, null.
 */
function mismoDocumentoEntre(frente: Hallazgo[], dorso: Hallazgo[]): { mismo: boolean | null; sospechosa: Lado } {
  for (const campo of ["numeroDocumento", "fechaNacimiento"]) {
    const c = contradiccionEn(frente, dorso, campo);
    if (c) return { mismo: false, sospechosa: c.cara };
  }
  const nF = deLaCara(frente, "numeroDocumento");
  const nD = deLaCara(dorso, "numeroDocumento");
  if (nF && nD && soloDigitos(nF.valor) === soloDigitos(nD.valor)) return { mismo: true, sospechosa: "reverso" };
  for (const campo of ["apellido", "nombres"]) {
    const a = deMaquina(frente, campo);
    const b = deMaquina(dorso, campo);
    if (a && b && !nombresCoherentes(a, b)) return { mismo: false, sospechosa: "reverso" };
  }
  return { mismo: null, sospechosa: "reverso" };
}

function confiable(c: CampoLeido | undefined, exigirPrueba: boolean): boolean {
  if (!c?.valor || c.verificacion === "en_conflicto") return false;
  return !exigirPrueba || PROBADA.has(c.verificacion);
}

/**
 * Las tres condiciones sobre las dos caras ya leídas.
 *
 * `exigirConfirmacion`: si (b) tiene que quedar confirmado (true) o alcanza con
 * que no haya contradicción (false).
 */
export function aceptarCaras(
  frente: Hallazgo[],
  dorso: Hallazgo[],
  opciones: { exigirConfirmacion?: boolean } = {}
): Aceptacion {
  const todos = consolidar([...frente, ...dorso]);
  const datosConfiables =
    confiable(todos.apellido, false) &&
    confiable(todos.nombres, false) &&
    confiable(todos.numeroDocumento, true) &&
    confiable(todos.fechaNacimiento, true);
  const { mismo: mismoDocumento, sospechosa } = mismoDocumentoEntre(frente, dorso);
  const legibles = caraConDatosBasicos(frente) || caraConDatosBasicos(dorso);

  let pedirOtra: Lado | null = null;
  if (mismoDocumento === false) {
    pedirOtra = sospechosa;
  } else if (!datosConfiables || !legibles) {
    // Sin la banda del dorso no hay de dónde probar nada: se pide el dorso. Con
    // la banda leída, lo que falta es del frente.
    pedirOtra = dorso.some((h) => h.origen === "mrz") ? "anverso" : "reverso";
  } else if (mismoDocumento === null && opciones.exigirConfirmacion) {
    pedirOtra = "anverso";
  }
  return { datosConfiables, mismoDocumento, legibles, aceptada: pedirOtra === null, pedirOtra };
}
